import { ActionIcon, Container, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconCheck, IconEdit, IconPlus, IconX } from '@tabler/icons-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { deliveryCompaniesQuery, useDeleteDeliveryCompany } from 'hooks/orders'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo } from 'react'
import { DeliveryCompany } from 'types/orders'

export default function DeliveryCompaniesTable() {
  const { data } = useSuspenseQuery(deliveryCompaniesQuery)

  const deleteDeliveryCompany = useDeleteDeliveryCompany()

  function openAddDeliveryCompanyModal() {
    modals.openContextModal({
      modal: 'addDeliveryCompany',
      centered: true,
      title: 'Dodaj kuriera',
      size: 1000,
      innerProps: {},
    })
  }

  function openUpdateDeliveryCompanyModal(deliveryCompany: DeliveryCompany) {
    modals.openContextModal({
      modal: 'updateDeliveryCompany',
      centered: true,
      title: 'Edytuj kuriera',
      size: 1000,
      innerProps: { deliveryCompany },
    })
  }

  function openDeleteDeliveryCompanyModal(deliveryCompany: DeliveryCompany) {
    modals.openConfirmModal({
      title: 'Usuń kuriera',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć kuriera <b>{deliveryCompany.name}</b>? Nie będzie można cofnąć tej operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteDeliveryCompany.mutateAsync(deliveryCompany),
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<DeliveryCompany>>>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nazwa',
      },
      {
        accessorKey: 'isActive',
        header: 'Aktywny',
        Cell: ({ cell }) => {
          return cell.getValue() ? (
            <IconCheck stroke={2.5} color="green" size={22} />
          ) : (
            <IconX stroke={2.5} color="red" size={22} />
          )
        },
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns,
    data,
    layoutMode: 'grid',
    editDisplayMode: 'table',
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: true,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableBottomToolbar: data.length < 5 ? false : true,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    pageCount: 5,
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10'],
    },
    initialState: {
      density: 'xs',
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 60,
      },
    },
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj metodę dostawy">
          <ActionIcon size="lg" variant="subtle" onClick={openAddDeliveryCompanyModal} ml="auto">
            <IconPlus />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Edytuj">
          <ActionIcon
            radius="xl"
            variant="light"
            color="gray"
            onClick={() => openUpdateDeliveryCompanyModal(row.original)}
          >
            <IconEdit size={18} />
          </ActionIcon>
        </Tooltip>
        {/* <Tooltip label="Usuń">
          <ActionIcon
            radius="xl"
            variant="light"
            color="red"
            onClick={() => openDeleteDeliveryCompanyModal(row.original)}
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip> */}
      </Flex>
    ),
  })
  return <MantineReactTable table={table} />
}
